import {
  BoxV4,
  PlanType,
  SelectV4,
  TextInputV4,
} from '@labrav/react-components'
import { type AccountDataSelection as AccountData } from '@labrav/common'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import aws from '../../common/images/aws.png'
import React from 'react'
import { useStyles } from './combinedFormHeader.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListDots } from '@fortawesome/pro-regular-svg-icons'
import { useDispatch, useSelector } from '../../store'
import { FormikProps, useFormikContext } from 'formik'
import {
  CombinedFormState,
  initialCombinedFormState as initialState,
} from '../../modules/combinedForm/reducer'
import {
  Product,
  ProductsListingState,
} from '../../modules/productsListing/reducer'
import { get } from 'lodash'
import { useEffect } from 'react'

interface CombinedFormHeaderProps {
  accounts: AccountData[]
  vendorName?: string
  onSelect: (product: Product) => void
  disabled?: boolean
}

export default function CombinedFormHeader({
  accounts,
  vendorName,
  onSelect,
  disabled,
}: CombinedFormHeaderProps) {
  const { classes } = useStyles()
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    resetForm,
  } = useFormikContext() as FormikProps<CombinedFormState>
  const { AWS } = useSelector<{ AWS: ProductsListingState }>(
    state => state.productsListing
  )
  const dispatch = useDispatch()
  const selectOptions = accounts.map(account => ({
    label: `${account.name} (${account.externalAccountId ? account.externalAccountId : account.id})`,
    value: account.id,
    key: account.id,
    imageUrl: aws,
  }))

  const searchParams = new URLSearchParams(window.location.search)
  const labraProductIdFromUrl = searchParams.get('labra_product_id')

  const isProductFromUrl = Boolean(labraProductIdFromUrl)

  useEffect(() => {
    if (!labraProductIdFromUrl || !AWS.products.rows.length) return

    const selectedProduct = AWS.products.rows.find(
      p => p.productId === labraProductIdFromUrl
    )

    if (!selectedProduct) return

    setFieldValue(
      'resellerAgreementData.awsProductId',
      selectedProduct.productId
    )
    setFieldValue(
      'resellerAgreementData.productType',
      selectedProduct.productType
    )
    setFieldValue(
      'resellerAgreementData.cloudProductUid',
      selectedProduct.awsProductId
    )

    onSelect(selectedProduct)
  }, [labraProductIdFromUrl, AWS.products.rows])

  const productSelectOptions = AWS.products.rows
    .filter(
      product =>
        product.productName &&
        [PlanType.SAAS_CONTRACT, PlanType.SAAS_SUBSCRIPTION].includes(
          product.productType as PlanType
        )
    )
    .map(product => ({
      label: product.productName,
      value: product.productId,
      key: product.productId,
    }))
  const handleAccountChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: string; name?: string } })
  ) => {
    handleChange(event)

    const selectedId = (event as any).target?.value as string
    const selectedAccount = accounts.find(acc => acc.id === selectedId)
    if (selectedAccount) {
      setFieldValue(
        'resellerAgreementData.resellersInfo[0].resellerAwsAccountId',
        selectedAccount.externalAccountId || ''
      )
    }
  }
  const handleProductChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: string; name?: string } })
  ) => {
    handleChange(event as any)

    const selectedId = (event as any).target?.value as string
    const selectedProduct = AWS.products.rows.find(
      p => p.productId === selectedId
    )
    if (selectedProduct) {
      onSelect(selectedProduct)
      setFieldValue(
        'resellerAgreementData.productType',
        selectedProduct.productType
      )
      setFieldValue(
        'resellerAgreementData.cloudProductUid',
        selectedProduct.awsProductId
      )
    }
    resetForm({
      values: {
        ...initialState,
        id: values.id,
        cppoData: { ...initialState.cppoData, eulaFile: [] },
        resellerAgreementData: {
          ...initialState.resellerAgreementData,
          awsProductId: selectedId,
          cloudProductUid: selectedProduct?.awsProductId as string,
          productType: selectedProduct?.productType || '',
          contractDurationInMonthsForEndCustomer: null,
          resellersInfo: [
            {
              ...initialState.resellerAgreementData.resellersInfo[0],
              resellerAwsAccountId:
                values.resellerAgreementData.resellersInfo?.[0]
                  ?.resellerAwsAccountId || '',
            },
          ],
          isvName: values.resellerAgreementData.isvName,
        },
        labraCpId: values.labraCpId,
        labraIsvId: values.labraIsvId,
      },
    })
  }

  return (
    <>
      <div className={classes.root}>
        <div className={classes.selector}>
          <SelectV4
            field={{
              title: 'Your cloud account',
              placeHolder: 'Your cloud account',
              id: 'labraCpId',
              isRequired: false,
              humanReadableKey: 'authorization',
              // isReadOnly: disabled || disableProductSelection,
              isReadOnly: disabled,

              isCustom: false,
              value: values.labraCpId,
              dataType: 'enum' as DataType,
              options: selectOptions,
              disabled: disabled,
            }}
            onChange={handleAccountChange}
            isError={
              !!(
                get(touched, 'labraCpId', false) &&
                get(errors, 'labraCpId', false)
              )
            }
            errorText={
              (!!get(touched, 'labraCpId', false) &&
                get(errors, 'labraCpId', '')) ||
              ''
            }
            size={'small'}
            minWidth="389px"
            onBlur={handleBlur}
          />
        </div>
        <BoxV4
          heading={'Select a product'}
          headingIcon={
            <FontAwesomeIcon
              icon={faListDots}
              style={{ width: '20px', height: '16px' }}
            />
          }
        >
          <div className={classes.sectionContainer}>
            <div className={classes.companyDetailsContainer}>
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="vendor"
                  title="Vendor name"
                  type="text"
                  placeHolder=""
                  value={vendorName || ''}
                  isError={false}
                  onChange={() => void 0}
                  isReadOnly={true}
                  isRequired={false}
                  errorText={''}
                  showSpinner={false}
                />
              </div>
              <div className={classes.inputWidth}>
                <SelectV4
                  field={{
                    title: 'Select a product',
                    placeHolder: 'Select a product',
                    id: 'resellerAgreementData.awsProductId',
                    isRequired: false,
                    humanReadableKey: 'product-selection',
                    isReadOnly: disabled || isProductFromUrl,
                    isCustom: false,
                    value: values.resellerAgreementData.awsProductId,
                    dataType: 'enum' as DataType,
                    options: productSelectOptions,
                    disabled: disabled || isProductFromUrl,
                  }}
                  onChange={handleProductChange}
                  showInsideInfoIcon={true}
                  showInsideInfoIconTooltip={{
                    title: '',
                    content:
                      'This field displays all the products listed by the selected ISV (Vendor) that are available for creating an offer.',
                  }}
                  isError={
                    !!(
                      get(
                        touched,
                        `resellerAgreementData.awsProductId`,
                        false
                      ) &&
                      get(errors, `resellerAgreementData.awsProductId`, false)
                    )
                  }
                  errorText={
                    (!!get(
                      touched,
                      `resellerAgreementData.awsProductId`,
                      false
                    ) &&
                      get(errors, `resellerAgreementData.awsProductId`, '')) ||
                    ''
                  }
                  size={'small'}
                  minWidth="389px"
                  onBlur={handleBlur}
                />
              </div>
            </div>
          </div>
        </BoxV4>
      </div>
    </>
  )
}
